<template>
  <div class="profile-page">
    <h1 class="subtitle-1 grey--text ml-3 mt-3">
      {{ $t("user.profile.title") }}
    </h1>
    <Progression v-if="loading || !user"></Progression>
    <div v-else class="user-info">
      <v-container fill-height fluid grid-list-xl>
        <v-layout justify-center wrap>
          <v-flex xs12 md4 lg3>
            <v-card text class="v-card-profile">
              <v-card-text>
                <v-avatar slot="offset" class="mx-auto d-block" size="130">
                  <!--
                  <img :src="user.avatar">
                  -->
                  <img
                    src="https://previews.123rf.com/images/yupiramos/yupiramos1705/yupiramos170527873/78659562-musician-playing-guitar-avatar-vector-illustration-design.jpg"
                  />
                </v-avatar>
                <v-card-text class="text-center">
                  <h6 v-if="!!user" class="text-gray font-weight-thin mb-3">
                    {{
                      !!user.role
                        ? $t("user.roles." + user.role + ".title")
                        : null
                    }}
                  </h6>
                  <h4 class="card-title font-weight-light">
                    {{ user.firstname }} {{ user.lastname }}
                  </h4>
                  <a :href="'mailto:' + user.email">{{ user.email }}</a>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="canEdit() && user.id != $store.state.site.user.id"
                    color="primary"
                    dark
                    :to="'/schedule?owner=' + user.id"
                  >
                    {{ $t("user.profile.buttons.see-schedule") }}
                  </v-btn>
                  <v-spacer></v-spacer>
                </v-card-actions>
              </v-card-text>
            </v-card>
          </v-flex>
          <v-flex xs12 md8 lg7>
            <v-card text title="Profile">
              <v-card-text>
                <h2>{{ $t("user.profile.general") }}</h2>
                <h3 v-if="user.deleted_at" style="color:#ed1c24;">
                  {{ $t("user.messages.deleted") }}
                </h3>
                <v-form ref="form" v-model="valid" lazy-validation>
                  <v-container>
                    <v-layout wrap>
                      <v-flex xs12>
                        <v-text-field
                          v-model="user.email"
                          readonly
                          :label="$t('user.fields.email')"
                          :rules="$rules('user.email')"
                        />
                      </v-flex>
                      <v-flex xs12>
                        <v-text-field
                          v-model="user.pastClasses"
                          readonly
                          :label="$t('user.fields.past-classes')"
                          type="number"
                        />
                      </v-flex>
                      <v-flex xs12 md6>
                        <v-text-field
                          v-model="user.firstname"
                          :disabled="!canEdit()"
                          :label="$t('user.fields.firstname')"
                          :rules="$rules('user.name')"
                        />
                      </v-flex>
                      <v-flex xs12 md6>
                        <v-text-field
                          v-model="user.lastname"
                          :disabled="!canEdit()"
                          :label="$t('user.fields.lastname')"
                          :rules="$rules('user.name')"
                        />
                      </v-flex>
                      <v-flex xs12 md6>
                        <v-text-field
                          v-model="user.phone1"
                          v-mask="'(###-###-####)'"
                          :disabled="!canEdit()"
                          :label="$t('user.fields.phone-1')"
                          :rules="$rules('user.phone1')"
                        />
                      </v-flex>
                      <v-flex xs12 md6>
                        <v-text-field
                          v-model="user.phone2"
                          v-mask="'(###-###-####)'"
                          :disabled="!canEdit()"
                          :label="$t('user.fields.phone-2')"
                          :rules="$rules('user.phone2')"
                        />
                      </v-flex>
                      <StudentFields
                        v-if="user.role === 'student'"
                        :data="user"
                        :can-edit="canEdit()"
                        :submitting="submitToChild"
                        @dataChanged="relationChanged"
                        @submitted="childSubmitted"
                      />
                      <TeacherFields
                        v-if="user.role === 'teacher' || user.role === 'admin'"
                        :data="user"
                        :can-edit="canEdit()"
                        :submitting="submitToChild"
                        @dataChanged="relationChanged"
                        @submitted="childSubmitted"
                      />
                    </v-layout>
                  </v-container>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-btn outlined @click="back">
                  {{ $t("app.buttons.back") }}
                </v-btn>

                <v-spacer></v-spacer>

                <DeleteButton
                  v-if="canDeactivate() && !user.deleted_at"
                  :message="$t('user.messages.delete')"
                  :name="$t('user.name')"
                  :soft="true"
                  :endpoint="'/users/' + user.id"
                  @deleted="back"
                ></DeleteButton>

                <v-btn
                  v-if="canEdit()"
                  :disabled="!pendingChanges || !valid"
                  dark
                  color="primary"
                  @click="submit"
                >
                  {{ $t("app.buttons.submit") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </div>
  </div>
</template>

<script>
import Progression from "@/components/Progression";
import StudentFields from "./fields/StudentFields";
import TeacherFields from "./fields/TeacherFields";
import DeleteButton from "@/components/utilities/DeleteButton";
import adaptersIn from "@/services/data-adapter/incoming/user";

export default {
  name: "Profile",

  components: {
    Progression,
    StudentFields,
    TeacherFields,
    DeleteButton
  },

  data() {
    return {
      loading: false,
      userId: this.$route.params.id,
      initUser: null,
      user: null,
      message: "",
      messageType: "error",
      childChanged: false,
      valid: true,
      submitToChild: false
    };
  },

  computed: {
    pendingChanges: function() {
      const result =
        !!this.childChanged ||
        !(
          this.user.firstname == this.initUser.firstname &&
          this.user.lastname == this.initUser.lastname &&
          this.user.email == this.initUser.email &&
          this.user.phone1 == this.initUser.phone1 &&
          this.user.phone2 == this.initUser.phone2
        );
      return result;
    }
  },

  mounted: function() {
    this.$nextTick(async function() {
      this.getProfile();
    });
  },

  methods: {
    permitted() {
      const role = this.$store.getters["site/user"].role;
      return !!role;
    },

    /**
     * If the current user can edit this profile.
     */
    canEdit() {
      const role = this.$store.getters["site/user"].role;
      return role === "admin" && !this.user.deleted_at;
    },

    /**
     * If the current user can deactivate this account
     */
    canDeactivate() {
      return this.$store.getters["site/user"].role === "admin";
    },

    /**
     * Fetch the profile data.
     */
    getProfile() {
      this.loading = true;
      var self = this;
      return new Promise((resolve, reject) => {
        this.$http
          .get("/users/" + this.userId + "?trashed=1")
          .then(response => {
            adaptersIn.user(response.data.user).then(res => {
              self.user = res;
              self.updateInitUser(self.user);
              self.loading = false;
              resolve(self.user);
            });
          })
          .catch(error => {
            if (process.env.NODE_ENV !== "production") {
              console.log(error);
            }
            this.$store.dispatch(
              "system/setStatus",
              {
                type: "error",
                message: "Could not retrieve this user's information."
              },
              { root: true }
            );
            return reject(false);
          });
      });
    },

    /**
     * When the child component has updates on its fields data.
     */
    relationChanged(bool) {
      this.childChanged = bool;
    },

    /**
     * This is where the custom field's response is sent after a submit.
     */
    childSubmitted(response) {
      this.$store.dispatch(
        "system/setStatus",
        {
          type: response.type,
          message: response.message
        },
        { root: true }
      );

      this.submitToChild = false;
    },

    /**
     * Updates the reference user object to compare for changes.
     */
    updateInitUser(user) {
      this.initUser = {
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        phone1: user.phone1,
        phone2: user.phone2,
        role: user.role
      };
    },

    /**
     * Tells the children that it can proceed to submit the data.
     */
    submit() {
      this.message = "";
      if (this.$refs.form.validate()) {
        this.submitToChild = true;
      }
    },

    back() {
      this.$router.go(-1);
    }
  }
};
</script>
